import { useEffect, useMemo, useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { getMyProfile, updateMyProfile } from '../../api/profile';
import { getLeaderIdAuthorizationUrl, getLeaderIdStatus } from '../../api/auth';
import styles from './settings.module.css';
import { SfeduSignIn } from '../AuthPage/SfeduSignIn';

const STUB_TAG = 'В разработке';

export function Settings() {
  const { isDark, toggleTheme } = useTheme();
  const [leaderLinked, setLeaderLinked] = useState(false);
  const [leaderStatusLoading, setLeaderStatusLoading] = useState(true);
  const [leaderActionLoading, setLeaderActionLoading] = useState(false);
  const [privacy, setPrivacy] = useState({
    showContacts: true,
    showEmail: true,
    showTelegram: true,
    showWebsite: true,
    showBio: true,
    showCity: true,
    searchable: true,
  });
  const [notifications, setNotifications] = useState({
    transfer: true,
    teams: true,
    cases: true,
    achievements: true,
    admin: true,
    inApp: true,
    email: false,
    telegram: false,
    quietHours: false,
    quietFrom: '23:00',
    quietTo: '08:00',
  });
  const [security, setSecurity] = useState({
    currentPassword: '',
    newPassword: '',
    repeatPassword: '',
  });
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileSaving, setProfileSaving] = useState(false);
  const [statusText, setStatusText] = useState('');

  const passwordMismatch = useMemo(() => {
    return Boolean(security.newPassword) && security.newPassword !== security.repeatPassword;
  }, [security.newPassword, security.repeatPassword]);

  const showStatus = (message) => {
    setStatusText(message);
    window.setTimeout(() => setStatusText(''), 2600);
  };

  useEffect(() => {
    let active = true;
    const loadProfile = async () => {
      setProfileLoading(true);
      setLeaderStatusLoading(true);
      try {
        const [profile, leaderStatus] = await Promise.all([
          getMyProfile(),
          getLeaderIdStatus(),
        ]);
        if (!active) return;
        setPrivacy((prev) => ({
          ...prev,
          showContacts: profile?.show_contacts !== false,
          showEmail: Boolean((profile?.show_contacts !== false) && profile?.email),
          showTelegram: Boolean((profile?.show_contacts !== false) && profile?.telegram),
          showWebsite: Boolean((profile?.show_contacts !== false) && profile?.website),
          showBio: true,
          showCity: true,
          searchable: true,
        }));
        setLeaderLinked(Boolean(leaderStatus?.linked));
      } catch {
        if (!active) return;
        showStatus('Не удалось загрузить часть настроек профиля.');
      } finally {
        if (active) {
          setProfileLoading(false);
          setLeaderStatusLoading(false);
        }
      }
    };
    loadProfile();
    return () => {
      active = false;
    };
  }, []);

  const saveContactsVisibility = async () => {
    setProfileSaving(true);
    try {
      await updateMyProfile({ show_contacts: Boolean(privacy.showContacts) });
      showStatus('Настройки приватности сохранены.');
    } catch {
      showStatus('Ошибка сохранения приватности. Попробуйте снова.');
    } finally {
      setProfileSaving(false);
    }
  };

  const changePrivacy = (key) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setPrivacy((prev) => ({ ...prev, [key]: value }));
  };

  const changeNotification = (key) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setNotifications((prev) => ({ ...prev, [key]: value }));
  };

  const changeSecurity = (key) => (event) => {
    const value = event.target.value;
    setSecurity((prev) => ({ ...prev, [key]: value }));
  };

  const handleLeaderIdLink = async () => {
    if (leaderLinked) {
      showStatus('Отвязка Leader-ID появится отдельной кнопкой после добавления API.');
      return;
    }

    try {
      setLeaderActionLoading(true);
      const payload = await getLeaderIdAuthorizationUrl();
      if (!payload?.authorization_url) {
        throw new Error('Authorization URL not returned.');
      }
      window.location.assign(payload.authorization_url);
    } catch {
      showStatus('Не удалось запустить привязку Leader-ID. Попробуйте снова.');
      setLeaderActionLoading(false);
    }
  };

  return (
    <section className={styles.page}>
      <h1 className={styles.title}>Настройки</h1>

      <div className={styles.card}>
        <div className={styles.row}>
          <div>
            <h2 className={styles.rowTitle}>Тема интерфейса</h2>
            <p className={styles.rowHint}>Можно переключаться между светлой и тёмной темой.</p>
          </div>
          <button
            type="button"
            className={`${styles.switch} ${isDark ? styles.switchOn : ''}`}
            onClick={toggleTheme}
            aria-pressed={isDark}
          >
            <span className={styles.thumb}></span>
          </button>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.row}>
          <div>
            <h2 className={styles.rowTitle}>Интеграции</h2>
            <p className={styles.rowHint}>Подключённые учётные записи для входа на платформу.</p>
          </div>
        </div>
        <SfeduSignIn link />
        <div className={styles.stack}>
          <div className={styles.subRow}>
            <div>
              <p className={styles.subTitle}>Leader-ID</p>
              <p className={styles.subHint}>
                Статус:{' '}
                {leaderStatusLoading
                  ? 'проверяем...'
                  : leaderLinked
                    ? 'аккаунт привязан'
                    : 'аккаунт не привязан'}
              </p>
            </div>
            <button
              type="button"
              className={styles.actionBtn}
              onClick={handleLeaderIdLink}
              disabled={leaderStatusLoading || leaderActionLoading}
            >
              {leaderActionLoading
                ? 'Переходим в Leader-ID...'
                : leaderLinked
                  ? 'Привязано'
                  : 'Привязать через Leader-ID'}
            </button>
          </div>
          <p className={styles.rowHint}>
            Привязка выполняется через авторизацию Leader-ID. Текущий статус берётся из сервера.
          </p>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.row}>
          <div>
            <h2 className={styles.rowTitle}>Профиль и приватность</h2>
            <p className={styles.rowHint}>Управление публичностью контактов и видимостью профиля.</p>
          </div>
        </div>

        <div className={styles.stack}>
          <div className={styles.subRow}>
            <label className={styles.toggleItem}>
              <input
                type="checkbox"
                checked={privacy.showContacts}
                onChange={changePrivacy('showContacts')}
                disabled={profileLoading || profileSaving}
              />
              <span>Показывать контакты в публичном профиле</span>
            </label>
            <button
              type="button"
              className={styles.actionBtn}
              onClick={saveContactsVisibility}
              disabled={profileLoading || profileSaving}
            >
              {profileSaving ? 'Сохраняем...' : 'Сохранить'}
            </button>
          </div>

          <p className={styles.rowHint}>
            Переключатель по умолчанию включен: при активном флаге email, Telegram, VK, MAX и сайт будут видны в публичном профиле.
          </p>

          <p className={styles.groupTitle}>Тонкая приватность контактов</p>
          <span className={styles.stubBadge}>{STUB_TAG}</span>
          <div className={styles.toggleGrid}>
            <label className={`${styles.toggleItem} ${styles.disabledItem}`}>
              <input type="checkbox" checked={privacy.showEmail} onChange={changePrivacy('showEmail')} disabled />
              <span>Показывать email</span>
            </label>
            <label className={`${styles.toggleItem} ${styles.disabledItem}`}>
              <input type="checkbox" checked={privacy.showTelegram} onChange={changePrivacy('showTelegram')} disabled />
              <span>Показывать telegram</span>
            </label>
            <label className={`${styles.toggleItem} ${styles.disabledItem}`}>
              <input type="checkbox" checked={privacy.showWebsite} onChange={changePrivacy('showWebsite')} disabled />
              <span>Показывать сайт</span>
            </label>
            <label className={`${styles.toggleItem} ${styles.disabledItem}`}>
              <input type="checkbox" checked={privacy.showBio} onChange={changePrivacy('showBio')} disabled />
              <span>Показывать bio</span>
            </label>
            <label className={`${styles.toggleItem} ${styles.disabledItem}`}>
              <input type="checkbox" checked={privacy.showCity} onChange={changePrivacy('showCity')} disabled />
              <span>Показывать город</span>
            </label>
            <label className={`${styles.toggleItem} ${styles.disabledItem}`}>
              <input type="checkbox" checked={privacy.searchable} onChange={changePrivacy('searchable')} disabled />
              <span>Показывать профиль в поиске</span>
            </label>
          </div>
          <p className={styles.rowHint}>Email/Telegram/Website и видимость в поиске: в разработке.</p>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.row}>
          <div>
            <h2 className={styles.rowTitle}>Уведомления</h2>
            <p className={styles.rowHint}>Типы, каналы доставки и тихие часы.</p>
          </div>
          <span className={styles.stubBadge}>{STUB_TAG}</span>
        </div>

        <div className={styles.stack}>
          <p className={styles.groupTitle}>Типы уведомлений</p>
          <div className={styles.toggleGrid}>
            <label className={`${styles.toggleItem} ${styles.disabledItem}`}><input type="checkbox" checked={notifications.transfer} onChange={changeNotification('transfer')} disabled /><span>Переводы</span></label>
            <label className={`${styles.toggleItem} ${styles.disabledItem}`}><input type="checkbox" checked={notifications.teams} onChange={changeNotification('teams')} disabled /><span>Команды</span></label>
            <label className={`${styles.toggleItem} ${styles.disabledItem}`}><input type="checkbox" checked={notifications.cases} onChange={changeNotification('cases')} disabled /><span>Кейсы</span></label>
            <label className={`${styles.toggleItem} ${styles.disabledItem}`}><input type="checkbox" checked={notifications.achievements} onChange={changeNotification('achievements')} disabled /><span>Ачивки</span></label>
            <label className={`${styles.toggleItem} ${styles.disabledItem}`}><input type="checkbox" checked={notifications.admin} onChange={changeNotification('admin')} disabled /><span>Админ-события</span></label>
          </div>

          <p className={styles.groupTitle}>Каналы</p>
          <div className={styles.toggleGrid}>
            <label className={`${styles.toggleItem} ${styles.disabledItem}`}><input type="checkbox" checked={notifications.inApp} onChange={changeNotification('inApp')} disabled /><span>Внутри платформы</span></label>
            <label className={`${styles.toggleItem} ${styles.disabledItem}`}><input type="checkbox" checked={notifications.email} onChange={changeNotification('email')} disabled /><span>Email</span></label>
            <label className={`${styles.toggleItem} ${styles.disabledItem}`}><input type="checkbox" checked={notifications.telegram} onChange={changeNotification('telegram')} disabled /><span>Telegram</span></label>
          </div>

          <div className={styles.quietRow}>
            <label className={`${styles.toggleItem} ${styles.disabledItem}`}>
              <input type="checkbox" checked={notifications.quietHours} onChange={changeNotification('quietHours')} disabled />
              <span>Тихие часы</span>
            </label>
            <div className={styles.timeRange}>
              <label className={styles.fieldSmall}>
                <span>С</span>
                <input type="time" value={notifications.quietFrom} onChange={changeNotification('quietFrom')} disabled />
              </label>
              <label className={styles.fieldSmall}>
                <span>До</span>
                <input type="time" value={notifications.quietTo} onChange={changeNotification('quietTo')} disabled />
              </label>
            </div>
          </div>

          <p className={styles.rowHint}>Центр уведомлений уже работает, расширенная конфигурация: в разработке.</p>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.row}>
          <div>
            <h2 className={styles.rowTitle}>Безопасность</h2>
            <p className={styles.rowHint}>Смена пароля, завершение всех сессий и 2FA.</p>
          </div>
          <span className={styles.stubBadge}>{STUB_TAG}</span>
        </div>

        <div className={styles.stack}>
          <div className={styles.formGrid}>
            <label className={styles.field}>
              <span>Текущий пароль</span>
              <input
                type="password"
                value={security.currentPassword}
                onChange={changeSecurity('currentPassword')}
                placeholder="Введите текущий пароль"
              />
            </label>
            <label className={styles.field}>
              <span>Новый пароль</span>
              <input
                type="password"
                value={security.newPassword}
                onChange={changeSecurity('newPassword')}
                placeholder="Введите новый пароль"
              />
            </label>
            <label className={styles.field}>
              <span>Повторите новый пароль</span>
              <input
                type="password"
                value={security.repeatPassword}
                onChange={changeSecurity('repeatPassword')}
                placeholder="Повторите новый пароль"
              />
            </label>
          </div>

          {passwordMismatch ? <p className={styles.errorText}>Пароли не совпадают.</p> : null}

          <div className={styles.actionsRow}>
            <button
              type="button"
              className={styles.actionBtn}
              disabled
              onClick={() => showStatus('Смена пароля будет подключена после добавления API.')}
            >
              Сменить пароль
            </button>
            <button
              type="button"
              className={styles.actionBtnGhost}
              disabled
              onClick={() => showStatus('Завершение всех сессий будет подключено к auth API.')}
            >
              Завершить все сессии
            </button>
          </div>

          <div className={styles.plannedBlock}>
            <p className={styles.groupTitle}>2FA</p>
            <p className={styles.rowHint}>Появится позже: TOTP и резервные коды.</p>
          </div>
        </div>
      </div>

      {statusText ? <p className={styles.statusText}>{statusText}</p> : null}
    </section>
  );
}
